import React, {useCallback, useEffect, useState} from 'react';
import Container from "../../components/UI/Container/Container";
import Block from "../../components/UI/Block/Block";
import {Button, Col, Divider, Empty, Flex, Input, message, Row, Space, Table, Tag, Tooltip, Typography} from "antd";
import {NEW_ENDPOINTS} from "../../http/endpoints";
import {API} from "../../http";
import moment from "moment";
import {ROUTES} from "../../components/AppRouter/AppRouter";
import {useNavigate} from "react-router-dom";
import TransactionsTable from "../../components/TransactionsTable/TransactionsTable";
import {debounce} from "lodash";
import {HomeOutlined, UserAddOutlined} from "@ant-design/icons";
import CreateUserModal from "./components/CreateUserModal/CreateUserModal";
import {PAYMENT_SYSTEM} from "../../utils/constants";

const Users = () => {
  const [searchParams, setSearchParams] = useState({
    id: '',
    email: '',
    firstname: '',
    lastname: ''
  });
  const [searchUsersLoading, setSearchUsersLoading] = useState(false);
  const [loadingTransactionsID, setLoadingTransactionsID] = useState();
  const [createUserModalVisible, setCreateUserModalVisible] = useState(false);
  const [users, setUsers] = useState([]);

  const navigate = useNavigate();

  const columns = [
    {
      title: 'Type',
      dataIndex: 'is_corporate',
      key: 'is_corporate',
      filters: [
        { text: 'Corporate', value: true },
        { text: 'Individual', value: false },
      ],
      filterMode: 'menu',
      width: 130,
      align: 'center',
      onFilter: (value, record) => record.is_corporate,
      render: (value) => {
        if (value) return <Tag bordered={false} color="volcano">Corporation</Tag>
        else return <Tag bordered={false} color="blue">Individual</Tag>
      }
    },
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 180,
      render: (value) => <Typography.Text copyable={value}
                                          ellipsis={{tooltip: value}}>{value || '-/-'}</Typography.Text>
    },
    {
      title: 'Name',
      key: 'name',
      width: 150,
      render: (value, record) => {
        const fullName = [record?.first_name, record?.last_name].filter(Boolean).join(' ');
        if (!fullName) return <Typography.Text>Unknown</Typography.Text>;
        return (
          <Typography.Link onClick={() => navigate(`${ROUTES.USER.path}/${record.id}`, {
            state: {user: record}
          })} copyable ellipsis>
            {fullName}
          </Typography.Link>
        );
      },
    },
    {
      title: 'E-mail',
      dataIndex: 'email_address',
      key: 'email_address',
      width: 250,
      render: (value) => <Typography.Text ellipsis={{tooltip: value}}
                                          copyable={value}>{value || '-/-'}</Typography.Text>
    },
    // {
    //   title: 'risk_level',
    //   dataIndex: 'risk_level',
    //   key: 'risk_level',
    //   width: 50,
    //   render: (value) => <Typography.Text>{value ?? '-/-'}</Typography.Text>
    // },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 150,
      render: (value) => <Typography.Text style={{width: 150}}
                                          ellipsis>{moment(value).format('DD.MM.YYYY, HH:mm:ss')}</Typography.Text>,
      defaultSortOrder: 'descend',
      sorter: (a, b) => {
        const dateA = moment(a.createdAt);
        const dateB = moment(b.createdAt);
        if (dateA < dateB) {
          return -1;
        } else if (dateA > dateB) {
          return 1;
        } else {
          return 0;
        }
      },
    },
  ];

  const fetchUsers = async () => {
    setSearchUsersLoading(true);
    try {
      const {data: {rr}} = await API.post(NEW_ENDPOINTS.ADMIN.USER.GET_LIST, searchParams);
      setUsers(rr);
    } catch (e) {
      message.error('Failed to load users list');
      setUsers([]);
    } finally {
      setSearchUsersLoading(false);
    }
  };
  const handleSearchChange = (changedFields) => {
    const newSearchParams = {
      ...searchParams,
      ...changedFields
    };
    setSearchParams(newSearchParams);
  };
  const debouncedSearch = useCallback(debounce(handleSearchChange, 300), []);
  useEffect(() => {
    fetchUsers();
  }, [searchParams]);

  const loadUserTransactions = async (user_id) => {
    setLoadingTransactionsID(user_id);
    try {
      const {data} = await API.post(NEW_ENDPOINTS.ADMIN.TRANSACTIONS.LIST, {
        user_id,
      });
      setUsers(prevUsers => prevUsers.map(user =>
        user.id === user_id ? {...user, transactions: data} : user
      ));
    } catch (error) {
      message.error('Failed to load transactions');
    } finally {
      setLoadingTransactionsID(undefined);
    }
  };
  const expandedRowRender = (record) => {
    const selectedUser = users.find(u => u.id === record.id);
    return <TransactionsTable excludeColumns={['client_name']} onStatusUpdate={() => loadUserTransactions(record.id)}
                              transactions={selectedUser?.transactions || []}
                              loading={loadingTransactionsID === record.id} pagination={false}/>
  };

  return (
    <Container>
      <Block>
        <Space direction="vertical" className="w100" size="small">
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={6}>
              <Input size="large" variant="filled" placeholder="ID"
                     onChange={(e) => debouncedSearch({id: e.target.value})}/>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Input size="large" variant="filled" placeholder="E-mail"
                     onChange={(e) => debouncedSearch({email: e.target.value})}/>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Input size="large" variant="filled" placeholder="First name"
                     onChange={(e) => debouncedSearch({firstname: e.target.value})}/>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Input size="large" variant="filled" placeholder="Last name"
                     onChange={(e) => debouncedSearch({lastname: e.target.value})}/>
            </Col>
          </Row>
        </Space>

        <Divider/>

        <Space className="w100" direction="vertical">
          <Flex style={{ marginBottom: 10 }} justify="flex-end" gap={10} className="w100" align="baseline">
            <Button onClick={() => navigate(ROUTES.CREATE_CORPORATION.path)} icon={<HomeOutlined />} shape="round" type="primary">
              Create Corporation
            </Button>
            <Button onClick={() => setCreateUserModalVisible(true)} icon={<UserAddOutlined />} shape="round" type="primary">
              Create User
            </Button>
          </Flex>

          <Table locale={{
            emptyText: <Empty description={false}/>,
          }} showSorterTooltip={false} pagination={{position: ['none', 'bottomCenter'], hideOnSinglePage: true}}
                 loading={searchUsersLoading} rowKey="id" dataSource={users} scroll={{x: 860}} columns={columns}
                 expandable={{
                   expandedRowRender,
                   onExpand: (expanded, record) => {
                     if (expanded && !record.transactions) loadUserTransactions(record.id)
                   },
                 }}/>
        </Space>
      </Block>

      <CreateUserModal onSubmit={fetchUsers} open={createUserModalVisible} onClose={() => setCreateUserModalVisible(false)}/>
    </Container>
  );
};

export default Users;
